import CriteriaService from "@/services/CriteriaService";

import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {CreateOwnCriteriaPopup} from "@popups/attentionPopups/CreateOwnCriteriaPopup";
import {useCallback, useEffect, useMemo, useState} from "react";

import {EditField} from "@ui/forms/EditField";
import {EditSelect} from "@ui/inputs/EditSelect";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";

import {ScenarioCriteriaList} from "./ScenarioCriteriaList";
import {
  buildScenarioCriteriaDisplayOptions,
  buildScenarioTemplateOptions,
  findDefaultEvaluationTemplate,
} from "./scenarioCriteriaHelpers";

export const ScenarioEvaluationTemplateCriteria = ({
  evaluationTemplateId,
  onEvaluationTemplateIdChange,
}) => {
  const {t} = useTranslation();
  const {openPopup} = usePopup();
  const [templates, setTemplates] = useState([]);
  const [templateTarget, setTemplateTarget] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const defaultTemplate = useMemo(
    () => findDefaultEvaluationTemplate(templates),
    [templates],
  );

  useEffect(() => {
    let cancelled = false;

    setLoadingTemplates(true);
    CriteriaService.listAllCriteria()
      .then((response) => {
        if (!cancelled) {
          setTemplates(response.data ?? []);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTemplates(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const applyTemplateById = useCallback(
    (nextId) => {
      if (!nextId) {
        onEvaluationTemplateIdChange(null);
        setTemplateTarget(null);
        return;
      }

      onEvaluationTemplateIdChange(nextId);
    },
    [onEvaluationTemplateIdChange],
  );

  useEffect(() => {
    if (loadingTemplates || evaluationTemplateId || !defaultTemplate) {
      return;
    }

    applyTemplateById(defaultTemplate.id);
  }, [
    loadingTemplates,
    evaluationTemplateId,
    defaultTemplate,
    applyTemplateById,
  ]);

  useEffect(() => {
    if (!evaluationTemplateId) {
      setTemplateTarget(null);
      return undefined;
    }

    let cancelled = false;

    setLoadingTemplate(true);
    CriteriaService.viewCriteria({id: evaluationTemplateId})
      .then((response) => {
        if (!cancelled) {
          setTemplateTarget(response.data);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTemplate(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [evaluationTemplateId]);

  const criteriaOptions = useMemo(
    () => buildScenarioCriteriaDisplayOptions(templateTarget),
    [templateTarget],
  );

  const templateOptions = useMemo(
    () => buildScenarioTemplateOptions(templates, t),
    [templates, t],
  );

  const handleTemplateSelect = useCallback(
    (selectedOption) => {
      if (selectedOption?.isCreateOwn) {
        openPopup(<CreateOwnCriteriaPopup />);

        if (defaultTemplate) {
          applyTemplateById(defaultTemplate.id);
        }

        return;
      }

      const nextId = selectedOption?.id ?? null;

      if (!nextId) {
        applyTemplateById(null);
        return;
      }

      applyTemplateById(nextId);
    },
    [applyTemplateById, defaultTemplate, openPopup],
  );

  return (
    <>
      <EditField
        label={t("input.criteria_name")}
        type={"custom"}
        height={150}
        customContent={
          <div className={"relative flex w-[500rem] items-center gap-[16rem]"}>
            {loadingTemplates ? (
              <Spinner
                width={24}
                height={24}
              />
            ) : (
              <EditSelect
                key={"evaluation_template_id"}
                resetKey={evaluationTemplateId ?? "empty"}
                height={150}
                searchPlaceholder={t("table.search_criteria")}
                activeOption={
                  evaluationTemplateId ? String(evaluationTemplateId) : ""
                }
                options={templateOptions}
                onSelect={handleTemplateSelect}
                emptyResult={false}
                multiSelect={false}
              />
            )}
          </div>
        }
      />

      {evaluationTemplateId && (
        <EditField
          label={`${t("input.criteria")}*`}
          type={"custom"}
          customContent={
            loadingTemplate ? (
              <Spinner
                width={32}
                height={32}
              />
            ) : (
              <ScenarioCriteriaList criteriaOptions={criteriaOptions} />
            )
          }
        />
      )}
    </>
  );
};
